"use client"

import { motion } from "framer-motion"
import { useEffect, useState } from "react"
import { getTokenFromStorage, getLocalIdFromToken } from "@/lib/auth"

interface MenuLoaderProps {
  onComplete?: () => void
}

export default function MenuLoader({ onComplete }: MenuLoaderProps) {
  const [progress, setProgress] = useState(0)
  const [isVisible, setIsVisible] = useState(true)
  const [establishmentName, setEstablishmentName] = useState("Carregando...")
  const [localId, setLocalId] = useState<string | null>(null)

  // Obter dados do token
  useEffect(() => {
    const token = getTokenFromStorage()
    if (token) {
      const extractedLocalId = getLocalIdFromToken(token)
      setLocalId(extractedLocalId)
    }
  }, [])

  // Carregar nome do estabelecimento
  useEffect(() => {
    if (!localId) return

    const cached = localStorage.getItem(`localData_${localId}`)
    if (cached) {
      const data = JSON.parse(cached)
      setEstablishmentName(data.estabelecimentoId?.nome || "Syncmenu")
    }

    fetch(`https://api.syncmenu.ao/generic/estabelecimento/${localId}`)
      .then((res) => res.json())
      .then((json) => {
        const content = json.localDeAtendimento
        setEstablishmentName(content.estabelecimentoId?.nome || "")
        localStorage.setItem(`localData_${localId}`, JSON.stringify(content))
      })
      .catch(() => {
        setEstablishmentName("")
      })
  }, [localId])

  useEffect(() => {
    // Simular progresso rápido
    const interval = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev + (100 - prev) * 0.15

        if (newProgress >= 99) {
          clearInterval(interval)

          // Quando o progresso estiver completo, aguardar um pouco e depois esconder
          setTimeout(() => {
            setIsVisible(false)
            if (onComplete) onComplete()
          }, 300)

          return 100
        }

        return newProgress
      })
    }, 100)

    return () => clearInterval(interval)
  }, [onComplete])

  // Animação de saída quando o carregamento estiver completo
  if (!isVisible) return null

  return (
    <motion.div
      initial={{ opacity: 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-b from-black to-gray-900"
    >
      <div className="w-24 h-24 mb-8 relative">
        {/* Prato girando */}
        <motion.div
          className="absolute inset-0"
          animate={{ rotate: 360 }}
          transition={{ duration: 3, repeat: Number.POSITIVE_INFINITY, ease: "linear" }}
        >
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <circle cx="50" cy="50" r="45" fill="none" stroke="#D4AF37" strokeWidth="2" />
            <circle cx="50" cy="50" r="35" fill="none" stroke="#D4AF37" strokeWidth="1" strokeDasharray="4 4" />
          </svg>
        </motion.div>

        {/* Ícone de talheres no centro */}
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.div
            animate={{
              scale: [1, 1.1, 1],
              opacity: [0.8, 1, 0.8],
            }}
            transition={{
              duration: 2,
              repeat: Number.POSITIVE_INFINITY,
              repeatType: "reverse",
            }}
            className="text-accent"
          >
            <svg viewBox="0 0 24 24" className="w-12 h-12" fill="currentColor">
              <path d="M11,9H9V2H7V9H5V2H3V9C3,11.12 4.66,12.84 6.75,12.97V22H9.25V12.97C11.34,12.84 13,11.12 13,9V2H11V9M16,6V14H18.5V22H21V2C18.24,2 16,4.24 16,6Z" />
            </svg>
          </motion.div>
        </div>
      </div>

      <div className="text-white text-xl font-medium mb-2">{establishmentName}</div>
      <div className="text-white/60 text-sm mb-6">Carregando Menu Digital</div>

      {/* Barra de progresso estilizada */}
      <div className="w-64 h-2 bg-gray-800 rounded-full overflow-hidden relative">
        <motion.div
          className="h-full bg-accent"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ type: "spring", stiffness: 50, damping: 10 }}
        />
      </div>

      <div className="text-white/60 text-sm mt-2">{Math.round(progress)}%</div>

      {/* Powered by */}
      <div className="absolute bottom-8 text-white/40 text-xs">
        Powered by
        <a href="http://www.syncmenu.ao">
          <span className="text-accent font-semibold">Syncmenu.ao</span>

        </a>
      </div>
    </motion.div>
  )
}
